import { useState, useEffect, useCallback, useMemo, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { pushNotification } from '@/lib/notifications';
import { extractChatMedia, firstChatImage } from '@/lib/aiChatMedia';
import { isOutOfStock, effectivePrice, type ChatBoutique, type ChatSearchResult } from '@/lib/chatResult';
import { DEFAULT_STORE_LOGO, storeLogoOr } from '@/lib/defaultImages';
import { normalizeCurrency } from '@/lib/currency';
import { fetchVendorMarketplace } from '@/lib/marketplaceCatalog';
import { trackBoutiqueView } from '@/lib/aiChatTracking';
import {
  listVendorReviews,
  createVendorReview,
  fetchVendorRating,
  sentimentMeta,
  formatVendorReviewDate,
  REVIEW_SENTIMENTS,
  type VendorReview,
  type ReviewSentiment,
} from '@/lib/vendorReviews';
import ChatMediaGallery from './ChatMediaGallery';

interface MerchantInfo extends ChatBoutique {
  description?: string | null;
}

interface Props {
  merchant: ChatBoutique;
  clientUserId: number | null;
  clientName?: string | null;
  lastQuery?: string;
  onClose: () => void;
  onAddToCart: (r: ChatSearchResult) => Promise<boolean> | boolean | void;
  onBook: (serviceId: number, serviceName: string) => void;
}

type Tab = 'catalogue' | 'services' | 'reserver' | 'avis' | 'message';

interface MsgRow {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  created_at: string;
}

const money = (value: number | null | undefined, cur: string | null | undefined) =>
  `${(Number(value) || 0).toLocaleString('fr-FR')} ${cur || 'MAD'}`;

/**
 * Fiche commerce ouverte DEPUIS le chat IA, sans quitter l'espace personnel.
 * On y retrouve le reste du catalogue du marchand (produits, services,
 * réservations) et on peut commander, réserver ou envoyer un message privé.
 */
export default function MerchantCardModal({
  merchant, clientUserId, clientName, lastQuery, onClose, onAddToCart, onBook,
}: Props) {
  const [tab, setTab] = useState<Tab>('catalogue');
  const [info, setInfo] = useState<MerchantInfo>(merchant);
  const [products, setProducts] = useState<ChatSearchResult[]>([]);
  const [services, setServices] = useState<ChatSearchResult[]>([]);
  const [bookingServices, setBookingServices] = useState<ChatSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChatSearchResult | null>(null);
  const [added, setAdded] = useState<number | null>(null);
  // Devise du vendeur : sert de référence de prix pour lever toute ambiguïté.
  const [merchantCurrency, setMerchantCurrency] = useState<string>('MAD');
  // Marketplace sur laquelle le vendeur est rattaché (là où il vend).
  const [market, setMarket] = useState<{ id: number; name: string } | null>(merchant.marketplace ?? null);

  const [messages, setMessages] = useState<MsgRow[]>([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [msgFeedback, setMsgFeedback] = useState<string | null>(null);

  // Avis boutique (onglet « Avis ») : liste, note moyenne et formulaire.
  const [reviews, setReviews] = useState<VendorReview[]>([]);
  const [rating, setRating] = useState<{ average: number; count: number }>({ average: 0, count: 0 });
  const [reviewForm, setReviewForm] = useState<{ name: string; comment: string; rating: number; sentiment: ReviewSentiment }>({
    name: clientName || '',
    comment: '',
    rating: 5,
    sentiment: 'positif',
  });
  const [reviewMsg, setReviewMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [reviewSending, setReviewSending] = useState(false);

  const merchantId = merchant.id;

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [infoRes, prodRes, svcRes, bookRes, annRes, marketRefRaw] = await Promise.all([
        supabase
          .from('users')
          .select('id, name, nomcommerce, user_name, telephone, Ville, Pays, image, description, monaie')
          .eq('id', merchantId)
          .maybeSingle(),
        supabase
          .from('product_items')
          .select('*, product_categories(id, name)')
          .eq('status', 'active')
          .eq('idcommerce', merchantId)
          .order('created_at', { ascending: false })
          .limit(24),
        supabase
          .from('nospartenairesservices')
          .select('id, titre, description, prix, product_image, ville, pays')
          .eq('idcommerce', merchantId)
          .order('id', { ascending: false })
          .limit(24),
        supabase
          .from('booking_services')
          .select('id, service_name, description, duration, location, image, statut')
          .eq('owner', merchantId)
          .eq('statut', 1)
          .limit(18),
        supabase
          .from('annonce')
          .select('id, titre, type, description, prix, image, video, ville, pays')
          .eq('idvendeur', merchantId)
          .eq('publique', 1)
          .in('type', ['produits', 'services'])
          .order('created', { ascending: false })
          .limit(36),
        fetchVendorMarketplace(merchantId),
      ]);

      const u = infoRes.data as Record<string, unknown> | null;
      const marketRef = marketRefRaw ?? null;
      setMarket(marketRef);
      if (u) {
        setMerchantCurrency(normalizeCurrency((u.monaie as string) || 'MAD'));
        setInfo({
          id: merchantId,
          name: (u.nomcommerce as string) || (u.name as string) || merchant.name,
          user_name: (u.user_name as string) || merchant.user_name,
          telephone: (u.telephone as string) || merchant.telephone,
          ville: (u.Ville as string) || merchant.ville,
          pays: (u.Pays as string) || merchant.pays,
          image: (u.image as string) || merchant.image,
          url: merchant.url,
          description: (u.description as string) || null,
          marketplace: marketRef,
        });
      }

      const boutique: ChatBoutique = {
        id: merchantId,
        name: ((u?.nomcommerce as string) || (u?.name as string) || merchant.name),
        user_name: (u?.user_name as string) || merchant.user_name,
        telephone: (u?.telephone as string) || merchant.telephone,
        ville: (u?.Ville as string) || merchant.ville,
        pays: (u?.Pays as string) || merchant.pays,
        image: (u?.image as string) || merchant.image,
        url: merchant.url,
        marketplace: marketRef,
      };

      const prodList: ChatSearchResult[] = ((prodRes.data || []) as Record<string, unknown>[]).map((p) => {
        const cat = p.product_categories as Record<string, unknown> | null;
        return {
          type: 'produit',
          id: p.id as number,
          name: p.name as string,
          description: (p.description as string) || undefined,
          price: Number(p.price) || 0,
          discount_price: p.discount_price != null ? Number(p.discount_price) : undefined,
          discount_enabled: Boolean(p.discount_enabled),
          currency: (p.currency as string) || null,
          stock: typeof p.stock === 'number' ? (p.stock as number) : null,
          stock_tracking_enabled: p.stock_tracking_enabled === true,
          product_type: (p.product_type as string) || 'physical',
          media: p.media as ChatSearchResult['media'],
          videos: p.videos as ChatSearchResult['videos'],
          ville: (p.ville as string) || undefined,
          pays: (p.pays as string) || undefined,
          category: (cat?.name as string) || undefined,
          boutique,
        };
      });

      const svcList: ChatSearchResult[] = ((svcRes.data || []) as Record<string, unknown>[]).map((s) => {
        // Un service peut porter PLUSIEURS visuels (chaîne séparée par « | »).
        const svcImages = ((s.product_image as string) || '')
          .split('|')
          .map((u) => u.trim())
          .filter(Boolean);
        return {
          type: 'service',
          id: s.id as number,
          name: (s.titre as string) || 'Service',
          description: (s.description as string) || undefined,
          price: Number(s.prix) || 0,
          image: svcImages[0] || undefined,
          media: svcImages.length > 0 ? svcImages : undefined,
          ville: (s.ville as string) || undefined,
          pays: (s.pays as string) || undefined,
          boutique,
        };
      });

      const bookList: ChatSearchResult[] = ((bookRes.data || []) as Record<string, unknown>[]).map((b) => ({
        type: 'service_réservation',
        id: b.id as number,
        name: (b.service_name as string) || 'Réservation',
        description: (b.description as string) || undefined,
        duration: b.duration != null ? Number(b.duration) : undefined,
        location: (b.location as string) || undefined,
        image: (b.image as string) || undefined,
        boutique,
      }));

      setProducts(prodList);
      setServices(svcList);
      setBookingServices(bookList);

      // Contenus marketplace (produits/services publiés dans `annonce`).
      const annRows = (annRes.data || []) as Record<string, unknown>[];
      // Une annonce peut porter PLUSIEURS images (table `annonces_images`) :
      // on les récupère pour afficher LA MÊME galerie multi-images que la fiche
      // produit, ici dans le catalogue du commerce.
      const annIds = annRows.map((a) => Number(a.id)).filter((n) => Number.isFinite(n) && n > 0);
      const annImagesMap: Record<number, string[]> = {};
      if (annIds.length > 0) {
        const { data: imgRows } = await supabase
          .from('annonces_images')
          .select('idannonce, image')
          .in('idannonce', annIds);
        (imgRows || []).forEach((im: Record<string, unknown>) => {
          const aid = Number(im.idannonce);
          const u = typeof im.image === 'string' ? im.image.trim() : '';
          if (!Number.isFinite(aid) || !u) return;
          if (!annImagesMap[aid]) annImagesMap[aid] = [];
          if (!annImagesMap[aid].includes(u)) annImagesMap[aid].push(u);
        });
      }
      const mediaOfAnnonce = (a: Record<string, unknown>): string[] => {
        const out: string[] = [];
        const main = typeof a.image === 'string' ? a.image.trim() : '';
        if (main) out.push(main);
        for (const u of annImagesMap[Number(a.id)] || []) if (!out.includes(u)) out.push(u);
        return out;
      };
      const extraProducts: ChatSearchResult[] = [];
      const extraServices: ChatSearchResult[] = [];
      annRows.forEach((a) => {
        const raw = String(a.type || '').toLowerCase();
        const base = {
          id: a.id as number,
          name: (a.titre as string) || 'Contenu',
          description: (a.description as string) || undefined,
          price: Number(a.prix) || 0,
          image: (a.image as string) || undefined,
          media: mediaOfAnnonce(a),
          videos: a.video as ChatSearchResult['videos'],
          ville: (a.ville as string) || undefined,
          pays: (a.pays as string) || undefined,
          boutique,
          source: 'marketplace' as const,
        };
        if (raw.startsWith('service')) extraServices.push({ ...base, type: 'service' });
        else extraProducts.push({ ...base, type: 'produit', product_type: 'physical' });
      });
      const mergedProducts = [
        ...prodList,
        ...extraProducts.filter((e) => !prodList.some((p) => p.id === e.id)),
      ];
      const mergedServices = [
        ...svcList,
        ...extraServices.filter((e) => !svcList.some((s) => s.id === e.id)),
      ];
      setProducts(mergedProducts);
      setServices(mergedServices);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement de la boutique');
    } finally {
      setLoading(false);
    }
  }, [merchant, merchantId]);

  const loadMessages = useCallback(async () => {
    if (!clientUserId) return;
    try {
      const { data } = await supabase
        .from('messages')
        .select('id, sender_id, receiver_id, message, created_at')
        .or(
          `and(sender_id.eq.${clientUserId},receiver_id.eq.${merchantId}),and(sender_id.eq.${merchantId},receiver_id.eq.${clientUserId})`,
        )
        .order('created_at', { ascending: true })
        .limit(200);
      setMessages((data || []) as MsgRow[]);
    } catch {
      /* fil indisponible : on continue */
    }
  }, [clientUserId, merchantId]);

  const loadReviews = useCallback(async () => {
    try {
      const [rows, rt] = await Promise.all([
        listVendorReviews(merchantId),
        fetchVendorRating(merchantId),
      ]);
      setReviews(rows);
      setRating(rt);
    } catch {
      /* best-effort */
    }
  }, [merchantId]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { if (tab === 'message') loadMessages(); }, [tab, loadMessages]);
  useEffect(() => { if (tab === 'avis') loadReviews(); }, [tab, loadReviews]);

  // 📊 Statistiques marchand : le client a OUVERT la fiche de cette boutique
  // depuis le chat IA. Le vendeur voit cette « boutique vue » dans son dashboard.
  useEffect(() => {
    if (!merchantId) return;
    trackBoutiqueView({
      merchantId,
      clientUserId: clientUserId ?? null,
      clientName: clientName || null,
      boutiqueName: merchant.name,
      query: lastQuery || null,
    });
  }, [merchantId, clientUserId, clientName, merchant.name, lastQuery]);

  const sendMessage = useCallback(async () => {
    const text = msgInput.trim();
    if (!text || !clientUserId || sending) return;
    setSending(true);
    setMsgFeedback(null);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .insert({ sender_id: clientUserId, receiver_id: merchantId, message: text, is_read: 0 })
        .select('id, sender_id, receiver_id, message, created_at')
        .maybeSingle();
      if (err) throw err;
      if (data) setMessages((prev) => [...prev, data as MsgRow]);
      setMsgInput('');
      setMsgFeedback('Message envoyé au commerce ✓');

      // 🔔 Cloche du marchand + badge de l'icône : nouveau message privé reçu.
      void pushNotification({
        userId: merchantId,
        senderId: clientUserId,
        publicationId: clientUserId,
        type: 'message',
        message: `Nouveau message de ${clientName || 'un client'} : ${text.slice(0, 120)}`,
      });

      // Notification Core (best-effort) pour que le marchand soit alerté.
      try {
        await supabase.from('core_notifications').insert({
          channel: 'in_app',
          type: 'client_private_message',
          title: 'Nouveau message privé d\'un client',
          body: text.slice(0, 300),
          entity_type: 'client_message',
          entity_id: String(clientUserId),
          recipient_user_id: merchantId,
          status: 'pending',
          metadata: { client_user_id: clientUserId, client_name: clientName || 'Client', query: lastQuery || null },
        });
      } catch { /* notification best-effort */ }
    } catch {
      setMsgFeedback("L'envoi a échoué. Réessayez.");
    } finally {
      setSending(false);
    }
  }, [msgInput, clientUserId, merchantId, sending, clientName, lastQuery]);

  const handleAdd = useCallback(async (r: ChatSearchResult) => {
    const ok = await onAddToCart(r);
    if (ok === false) return;
    setAdded(r.id);
    setTimeout(() => setAdded(null), 1800);
  }, [onAddToCart]);

  // Dépôt d'un avis sur la boutique (note + sentiment + commentaire).
  const submitReview = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!clientUserId) return;
    const name = reviewForm.name.trim() || clientName || 'Client';
    if (reviewForm.comment.trim().length < 5) {
      setReviewMsg({ type: 'err', text: 'Votre commentaire est un peu court.' });
      return;
    }
    setReviewSending(true);
    setReviewMsg(null);
    try {
      await createVendorReview({
        commerceId: merchantId,
        authorUserId: clientUserId,
        authorName: name,
        rating: reviewForm.rating,
        sentiment: reviewForm.sentiment,
        comment: reviewForm.comment.trim(),
      });
      setReviewMsg({ type: 'ok', text: 'Merci ! Votre avis est publié et participe au classement des meilleurs commerces.' });
      setReviewForm((f) => ({ ...f, comment: '' }));
      await loadReviews();
    } catch (err) {
      setReviewMsg({ type: 'err', text: err instanceof Error ? err.message : 'Envoi impossible pour le moment.' });
    } finally {
      setReviewSending(false);
    }
  }, [clientUserId, clientName, reviewForm, merchantId, loadReviews]);

  const gridStyle = 'grid grid-cols-2 sm:grid-cols-3 gap-3';
  const cardStyle = 'bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-300 transition-colors';

  const tabs: { key: Tab; label: string; icon: string; count?: number }[] = useMemo(() => ([
    { key: 'catalogue', label: 'Produits', icon: 'ri-shopping-bag-3-line', count: products.length },
    { key: 'services', label: 'Services', icon: 'ri-service-line', count: services.length },
    { key: 'reserver', label: 'Réserver', icon: 'ri-calendar-check-line', count: bookingServices.length },
    { key: 'avis', label: 'Avis', icon: 'ri-star-smile-line', count: rating.count },
    { key: 'message', label: 'Message', icon: 'ri-chat-3-line' },
  ]), [products.length, services.length, bookingServices.length, rating.count]);

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-3 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl max-h-[92vh] bg-background-50 rounded-xl overflow-hidden flex flex-col">
        {/* En-tête commerce */}
        <div className="relative flex-shrink-0 p-4 border-b border-background-200/70 bg-gradient-to-r from-background-100 to-background-50">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors z-10"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-foreground-700"></i>
          </button>
          <div className="flex items-center gap-3 min-w-0 pr-10">
            <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-background-200/70">
              <img
                src={storeLogoOr(info.image)}
                alt={info.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (img.src !== DEFAULT_STORE_LOGO) img.src = DEFAULT_STORE_LOGO;
                }}
              />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{info.name}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                {(info.ville || info.pays) && (
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {[info.ville, info.pays].filter(Boolean).join(', ')}
                  </span>
                )}
                {info.telephone && (
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-phone-line"></i>{info.telephone}
                  </span>
                )}
                {market?.name && (
                  <span className="text-xs text-primary-700 bg-primary-50 border border-primary-200/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <i className="ri-store-3-line"></i>Vend sur {market.name}
                  </span>
                )}
                <span className="text-xs text-secondary-700 bg-secondary-50 border border-secondary-200/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <i className="ri-coins-line"></i>Prix en {merchantCurrency}
                </span>
              </div>
            </div>
          </div>
          {info.description && (
            <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{info.description}</p>
          )}
        </div>

        {/* Onglets */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-background-200/70 overflow-x-auto flex-shrink-0">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => { setTab(tb.key); setDetail(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                tab === tb.key
                  ? 'bg-accent-100 text-accent-700'
                  : 'text-foreground-500 hover:bg-background-100'
              }`}
            >
              <i className={tb.icon}></i>
              {tb.label}
              {tb.count != null && tb.count > 0 && (
                <span className="px-1.5 rounded-full bg-background-200/70 text-foreground-600 text-[10px]">{tb.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
              <p className="text-sm text-foreground-600 mb-3">{error}</p>
              <button
                onClick={loadCatalog}
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : detail ? (
            /* Détail produit (interne à la fiche commerce) */
            <div className="max-w-lg mx-auto">
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 mb-3 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>Retour au catalogue
              </button>

              <ChatMediaGallery
                media={extractChatMedia({ media: detail.media, image: detail.image, videos: detail.videos })}
                alt={detail.name}
                coverClassName="h-52 sm:h-64"
              />

              <div className="mt-4">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-lg font-bold font-heading text-foreground-950">{detail.name}</h4>
                  <div className="text-right flex-shrink-0">
                    {detail.discount_enabled && detail.discount_price ? (
                      <>
                        <div className="text-lg font-bold text-primary-600 whitespace-nowrap">{money(detail.discount_price, detail.currency || merchantCurrency)}</div>
                        <div className="text-xs text-foreground-400 line-through whitespace-nowrap">{money(detail.price, detail.currency || merchantCurrency)}</div>
                      </>
                    ) : (
                      <div className="text-lg font-bold text-primary-600 whitespace-nowrap">{money(detail.price, detail.currency || merchantCurrency)}</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {detail.category && (
                    <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">{detail.category}</span>
                  )}
                  {detail.type === 'produit' && typeof detail.stock === 'number' && detail.stock_tracking_enabled && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isOutOfStock(detail) ? 'bg-red-50 text-red-600' : 'bg-accent-50 text-accent-700'}`}>
                      {isOutOfStock(detail) ? 'Rupture de stock' : `${detail.stock} en stock`}
                    </span>
                  )}
                </div>

                {detail.description && (
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3 whitespace-pre-line">{detail.description}</p>
                )}

                {detail.type === 'service' ? (
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => handleAdd(detail)}
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <i className={added === detail.id ? 'ri-check-line' : 'ri-shopping-cart-2-line'}></i>
                      {added === detail.id ? 'Ajouté au panier !' : 'Demander ce service'}
                    </button>
                    {bookingServices.length > 0 && (
                      <button
                        type="button"
                        onClick={() => onBook(bookingServices[0].id, detail.name)}
                        className="w-full py-3 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-calendar-check-line"></i>
                        Réserver ce service
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={isOutOfStock(detail)}
                    onClick={() => handleAdd(detail)}
                    className={`w-full mt-4 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-colors whitespace-nowrap ${
                      isOutOfStock(detail)
                        ? 'bg-background-200/70 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                    }`}
                  >
                    <i className={added === detail.id ? 'ri-check-line' : 'ri-shopping-cart-2-line'}></i>
                    {isOutOfStock(detail) ? 'Produit en rupture de stock' : added === detail.id ? 'Ajouté au panier !' : 'Commander'}
                  </button>
                )}
              </div>
            </div>
          ) : tab === 'catalogue' ? (
            products.length === 0 ? (
              <EmptyBlock icon="ri-shopping-bag-3-line" text="Aucun produit pour ce commerce" />
            ) : (
              <div className={gridStyle}>
                {products.map((p) => {
                  const thumb = firstChatImage({ media: p.media, image: p.image, videos: p.videos });
                  const out = isOutOfStock(p);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setDetail(p)}
                      className={`${cardStyle} text-left cursor-pointer`}
                    >
                      <div className="relative h-32 bg-background-100 overflow-hidden">
                        {thumb ? (
                          <img src={thumb} alt={p.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><i className="ri-image-line text-2xl text-foreground-300"></i></div>
                        )}
                        {out && (
                          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-red-500 text-white rounded text-[10px] font-semibold">Rupture</span>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-semibold text-foreground-900 line-clamp-2">{p.name}</p>
                        <p className="text-sm font-bold text-primary-600 mt-1 whitespace-nowrap">{money(effectivePrice(p), p.currency || merchantCurrency)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : tab === 'services' ? (
            services.length === 0 ? (
              <EmptyBlock icon="ri-service-line" text="Aucun service pour ce commerce" />
            ) : (
              <div className="space-y-2">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setDetail(s)}
                    className="w-full flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3 text-left cursor-pointer hover:border-accent-300 transition-colors"
                  >
                    <div className="w-14 h-14 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                      {s.image ? (
                        <img src={s.image} alt={s.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><i className="ri-service-line text-foreground-300"></i></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{s.name}</p>
                      {s.description && <p className="text-xs text-foreground-500 line-clamp-1">{s.description}</p>}
                      <p className="text-sm font-bold text-primary-600 mt-0.5 whitespace-nowrap">{money(s.price, merchantCurrency)}</p>
                    </div>
                    <span className="text-xs text-accent-600 font-semibold whitespace-nowrap flex items-center gap-0.5 flex-shrink-0">
                      Voir le service<i className="ri-arrow-right-s-line"></i>
                    </span>
                  </button>
                ))}
              </div>
            )
          ) : tab === 'reserver' ? (
            bookingServices.length === 0 ? (
              <EmptyBlock icon="ri-calendar-check-line" text="Aucune réservation en ligne pour ce commerce" />
            ) : (
              <div className="space-y-2">
                {bookingServices.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
                    <div className="w-14 h-14 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                      {b.image ? (
                        <img src={b.image} alt={b.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><i className="ri-calendar-check-line text-foreground-300"></i></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{b.name}</p>
                      <p className="text-xs text-foreground-500">
                        {b.duration ? `${b.duration} min` : '—'}{b.location ? ` · ${b.location}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onBook(b.id, b.name)}
                      className="px-3 py-2 bg-primary-500 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
                    >
                      Réserver
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : tab === 'avis' ? (
            <div className="space-y-4">
              {/* Résumé de la note (classement des meilleurs commerces/vendeurs) */}
              <div className="flex items-center gap-4 p-4 bg-background-100/60 rounded-lg">
                <div className="text-center flex-shrink-0">
                  <div className="text-3xl font-bold font-heading text-foreground-950">{rating.average.toFixed(1)}</div>
                  <StarsRow rating={rating.average} />
                  <div className="text-[11px] text-foreground-500 mt-0.5">{rating.count} avis</div>
                </div>
                <div className="flex-1 text-xs text-foreground-500 leading-relaxed">
                  Votre avis aide à classer les meilleurs commerces et vendeurs de la plateforme.
                </div>
              </div>

              {/* Formulaire de dépôt d'avis */}
              {!clientUserId ? (
                <div className="px-4 py-3 bg-background-100 rounded-lg text-sm text-foreground-500 text-center">
                  Connectez-vous pour laisser un avis.
                </div>
              ) : (
                <form onSubmit={submitReview} className="p-4 rounded-lg border border-background-200/70 bg-background-50 space-y-3">
                  <div>
                    <span className="block text-xs font-medium text-foreground-600 mb-1.5">Votre note</span>
                    <InteractiveStars value={reviewForm.rating} onChange={(v) => setReviewForm((f) => ({ ...f, rating: v }))} />
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-foreground-600 mb-1.5">Votre ressenti</span>
                    <div className="flex flex-wrap gap-2">
                      {REVIEW_SENTIMENTS.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setReviewForm((f) => ({ ...f, sentiment: s.key }))}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                            reviewForm.sentiment === s.key ? s.className : 'bg-background-50 text-foreground-500 border-background-200/70 hover:bg-background-100'
                          }`}
                        >
                          <i className={`${s.icon}`}></i>{s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Votre nom"
                    maxLength={80}
                    className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
                  />
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                    placeholder="Partagez votre expérience…"
                    maxLength={1000}
                    className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 min-h-[80px]"
                  ></textarea>
                  {reviewMsg && (
                    <p className={`text-xs ${reviewMsg.type === 'ok' ? 'text-accent-700' : 'text-red-500'}`}>{reviewMsg.text}</p>
                  )}
                  <button
                    type="submit"
                    disabled={reviewSending}
                    className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {reviewSending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                    Publier mon avis
                  </button>
                </form>
              )}

              {/* Liste des avis */}
              {reviews.length === 0 ? (
                <EmptyBlock icon="ri-star-smile-line" text="Aucun avis pour le moment. Soyez le premier !" />
              ) : (
                <ul className="space-y-3">
                  {reviews.map((r) => {
                    const sm = sentimentMeta(r.sentiment);
                    return (
                      <li key={r.id} className="p-3 rounded-lg border border-background-200/70 bg-background-50">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="w-7 h-7 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {r.authorName.charAt(0).toUpperCase()}
                          </span>
                          <span className="text-sm font-semibold text-foreground-900">{r.authorName}</span>
                          {sm && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sm.className}`}>
                              <i className={sm.icon}></i>{sm.label}
                            </span>
                          )}
                          <span className="ml-auto text-[11px] text-foreground-400">{formatVendorReviewDate(r.createdAt)}</span>
                        </div>
                        <div className="mt-1.5"><StarsRow rating={r.rating} /></div>
                        {r.comment && <p className="text-sm text-foreground-600 leading-relaxed mt-1.5 whitespace-pre-line">{r.comment}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            /* Messagerie privée */
            <div className="flex flex-col h-full">
              <div className="flex-1 min-h-[180px] max-h-[44vh] overflow-y-auto space-y-2 mb-3 pr-1">
                {messages.length === 0 ? (
                  <div className="text-center text-foreground-400 py-10">
                    <i className="ri-chat-3-line text-3xl mb-2"></i>
                    <p className="text-sm">Écrivez votre premier message à {info.name}</p>
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === clientUserId;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] px-3 py-2 rounded-lg text-sm ${mine ? 'bg-accent-100/70 text-foreground-800' : 'bg-background-100 text-foreground-800'}`}>
                          <p style={{ whiteSpace: 'pre-wrap' }}>{m.message}</p>
                          <p className="text-[10px] text-foreground-400 mt-1">
                            {new Date(m.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {msgFeedback && (
                <p className={`text-xs mb-2 ${msgFeedback.includes('échoué') ? 'text-red-500' : 'text-accent-700'}`}>{msgFeedback}</p>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={msgInput}
                  onChange={(e) => setMsgInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                  placeholder="Votre message privé..."
                  className="flex-1 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm focus:outline-none focus:border-accent-300"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!msgInput.trim() || sending}
                  className="w-10 h-10 rounded-full bg-accent-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  <i className={sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyBlock({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-40 text-foreground-400">
      <i className={`${icon} text-3xl mb-2`}></i>
      <p className="text-sm">{text}</p>
    </div>
  );
}

/** Étoiles de notation (lecture seule). */
function StarsRow({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={`${n <= full ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} text-sm`}></i>
      ))}
    </span>
  );
}

/** Étoiles cliquables (saisie de la note). */
function InteractiveStars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          className="cursor-pointer transition-transform hover:scale-110"
          aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
        >
          <i className={`${n <= shown ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} text-2xl`}></i>
        </button>
      ))}
    </div>
  );
}